import React, { useState, useEffect } from 'react'
import { Save, Edit, FileText, Home, Info, Phone, BookOpen, Users } from 'lucide-react'
import { useContent } from '@context/ContentContext'

const ContentManager = () => {
  const { content, updatePage, loading } = useContent()
  const [selectedPage, setSelectedPage] = useState('home')
  const [isEditing, setIsEditing] = useState(false)
  const [editingContent, setEditingContent] = useState({})
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')

  const pages = [
    { id: 'home', name: 'Home Page', icon: Home },
    { id: 'about', name: 'About Page', icon: Info },
    { id: 'services', name: 'Services Page', icon: FileText },
    { id: 'resources', name: 'Resources Page', icon: BookOpen },
    { id: 'contact', name: 'Contact Page', icon: Phone },
    { id: 'careers', name: 'Careers Page', icon: Users }
  ]

  // Get current page content from Firebase
  const getCurrentPageContent = () => {
    const pageData = content.pages?.[selectedPage]
    if (!pageData) {
      // Return default content structure if page doesn't exist
      return {
        hero: {
          title: '',
          subtitle: ''
        }
      }
    }
    // Remove the id field from the page data for editing
    const { id, ...pageContent } = pageData
    return pageContent
  }

  const handleEdit = () => {
    const pageContent = getCurrentPageContent()
    setEditingContent({ ...pageContent })
    setIsEditing(true)
    setMessage('')
  }

  const handleSave = async () => {
    setSaving(true)
    setMessage('')

    try {
      // Check if page exists in Firebase
      const existingPage = content.pages?.[selectedPage]
      
      if (existingPage) {
        // Update existing page
        await updatePage(selectedPage, editingContent)
      } else {
        // Create new page
        const newPageData = {
          id: selectedPage,
          ...editingContent
        }
        await updatePage(selectedPage, newPageData)
      }
      
      setMessage('Content updated successfully!')
      setIsEditing(false)
      setEditingContent({})
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000)
    } catch (error) {
      console.error('Error updating content:', error)
      setMessage('Error updating content: ' + error.message)
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setEditingContent({})
    setIsEditing(false)
    setMessage('')
  }

  const updateField = (field, value) => {
    // Handle nested fields like hero.title
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setEditingContent(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else {
      setEditingContent(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  const renderContentFields = () => {
    const pageContent = isEditing ? editingContent : getCurrentPageContent()
    
    const renderNestedFields = (obj, prefix = '') => {
      return Object.entries(obj).map(([key, value]) => {
        const fieldKey = prefix ? `${prefix}.${key}` : key
        
        if (Array.isArray(value)) {
          return (
            <div key={fieldKey} className="space-y-4">
              <h4 className="text-lg font-semibold text-navy-900 capitalize border-b border-neutral-200 pb-2">
                {key.replace(/([A-Z])/g, ' $1').trim()}
              </h4>
              <div className="ml-4 p-4 bg-neutral-50 rounded-xl border border-neutral-200">
                <p className="text-sm text-muted mb-2">
                  Array content (contains {value.length} items)
                </p>
                <p className="text-xs text-neutral-600">
                  Complex array structures are managed through the Services Manager for services data.
                </p>
              </div>
            </div>
          )
        }
        
        if (typeof value === 'object' && value !== null) {
          return (
            <div key={fieldKey} className="space-y-4">
              <h4 className="text-lg font-semibold text-navy-900 capitalize border-b border-neutral-200 pb-2">
                {key.replace(/([A-Z])/g, ' $1').trim()}
              </h4>
              <div className="ml-4 space-y-4">
                {renderNestedFields(value, fieldKey)}
              </div>
            </div>
          )
        }
        
        return (
          <div key={fieldKey}>
            <label className="block text-sm font-semibold text-navy-900 mb-2 capitalize">
              {key.replace(/([A-Z])/g, ' $1').trim()}
            </label>
            {isEditing ? (
              key.includes('Description') || key.includes('Subtitle') || key.includes('subtitle') ? (
                <textarea
                  value={value || ''}
                  onChange={(e) => updateField(fieldKey, e.target.value)}
                  className="form-textarea"
                  rows={4}
                  placeholder={`Enter ${key.replace(/([A-Z])/g, ' $1').trim().toLowerCase()}`}
                />
              ) : (
                <input
                  type="text"
                  value={value || ''}
                  onChange={(e) => updateField(fieldKey, e.target.value)}
                  className="form-input"
                  placeholder={`Enter ${key.replace(/([A-Z])/g, ' $1').trim().toLowerCase()}`}
                />
              )
            ) : (
              <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-200">
                <p className="text-navy-900">{value || 'No content set'}</p>
              </div>
            )}
          </div>
        )
      })
    }
    
    return (
      <div className="space-y-6">
        {renderNestedFields(pageContent)}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-navy-200 border-t-navy-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted">Loading content...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-display-3 text-navy-900 mb-2">Content Management</h1>
          <p className="text-body text-muted">Edit and manage content across all pages.</p>
        </div>
        {!isEditing ? (
          <button onClick={handleEdit} className="btn-primary">
            <Edit className="w-4 h-4 mr-2" />
            Edit Content
          </button>
        ) : (
          <div className="flex space-x-4">
            <button 
              onClick={handleSave} 
              className="btn-primary"
              disabled={saving}
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button onClick={handleCancel} className="btn-secondary">
              Cancel
            </button>
          </div>
        )}
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-xl ${
          message.includes('Error') 
            ? 'bg-red-50 text-red-700 border border-red-200' 
            : 'bg-green-50 text-green-700 border border-green-200'
        }`}>
          <div className="flex items-center space-x-2">
            <span>{message}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Page Selector */}
        <div className="lg:col-span-1">
          <div className="card p-6">
            <h3 className="text-heading-2 text-navy-900 mb-6">Select Page</h3>
            <div className="space-y-2">
              {pages.map((page) => (
                <button
                  key={page.id}
                  onClick={() => setSelectedPage(page.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 text-left ${
                    selectedPage === page.id
                      ? 'bg-navy-50 text-navy-800 border border-navy-200'
                      : 'text-neutral-600 hover:text-navy-800 hover:bg-neutral-50'
                  }`}
                >
                  <page.icon className="w-5 h-5" />
                  <span className="font-medium">{page.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content Editor */}
        <div className="lg:col-span-3">
          <div className="card p-8">
            <div className="flex items-center space-x-3 mb-6">
              {pages.find(p => p.id === selectedPage)?.icon && (
                React.createElement(pages.find(p => p.id === selectedPage).icon, { className: "w-6 h-6 text-navy-700" })
              )}
              <h3 className="text-heading-2 text-navy-900">
                {pages.find(p => p.id === selectedPage)?.name} Content
              </h3>
            </div>

            {renderContentFields()}

            {isEditing && (
              <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <h4 className="font-semibold text-blue-900 mb-2">Preview Changes</h4>
                <p className="text-sm text-blue-700">
                  Your changes will be reflected on the live website after saving. 
                  Make sure to review your content before saving.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Guidelines */}
      <div className="mt-8 card p-6">
        <h3 className="text-heading-2 text-navy-900 mb-4">Content Guidelines</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
            <h4 className="font-semibold text-green-900 mb-2">Best Practices</h4>
            <ul className="text-sm text-green-700 space-y-1">
              <li>• Keep titles concise and impactful</li>
              <li>• Use clear, professional language</li>
              <li>• Focus on client benefits</li>
              <li>• Maintain consistent tone</li>
            </ul>
          </div>
          
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <h4 className="font-semibold text-blue-900 mb-2">SEO Tips</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Include relevant keywords</li>
              <li>• Write compelling meta descriptions</li>
              <li>• Use action-oriented language</li>
              <li>• Keep titles under 60 characters</li>
            </ul>
          </div>
          
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl">
            <h4 className="font-semibold text-purple-900 mb-2">Brand Voice</h4>
            <ul className="text-sm text-purple-700 space-y-1">
              <li>• Professional yet approachable</li>
              <li>• Expert and authoritative</li>
              <li>• Solution-focused</li>
              <li>• Client-centric messaging</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContentManager 